import { c as _c } from "react/compiler-runtime";
import { Donation } from 'api-graphql-types';
import { useCallback, useMemo, useState } from 'react';
import { ApiRouteNames, useApi } from '@/api';
function useGetDonationListData(fundraisingEventId, itemsPerVirtualPage, t0, onLoaded) {
  const $ = _c(9);
  const cursor = t0 === undefined ? "" : t0;
  const t1 = String(itemsPerVirtualPage * 5);
  let t2;
  if ($[0] !== cursor || $[1] !== fundraisingEventId || $[2] !== t1) {
    t2 = {
      slug: fundraisingEventId,
      cursor,
      direction: "before",
      itemsPerPage: t1
    };
    $[0] = cursor;
    $[1] = fundraisingEventId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onLoaded) {
    t3 = {
      after: onLoaded
    };
    $[4] = onLoaded;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data,
    isLoading,
    isValidating
  } = useApi(ApiRouteNames.apiFundraisingEventDonationList, t2, t3);
  const t4 = isLoading || isValidating;
  let t5;
  if ($[6] !== data || $[7] !== t4) {
    t5 = {
      data,
      isLoading: t4
    };
    $[6] = data;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function useDonationListData(fundraisingEventId, itemsPerVirtualPage) {
  const $ = _c(64);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [displayableDonations, setDisplayableDonations] = useState(t0);
  const [cursor, setCursor] = useState(undefined);
  const [offset, setOffset] = useState(0);
  const [virtualPageIndex, setVirtualPageIndex] = useState(-1);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [previouslyDisplayedDonations, setPreviouslyDisplayedDonations] = useState(t1);
  const maxOffset = 4 * itemsPerVirtualPage;
  const minOffset = -5 * itemsPerVirtualPage;
  let t2;
  if ($[2] !== itemsPerVirtualPage || $[3] !== maxOffset || $[4] !== minOffset) {
    t2 = () => {
      setOffset(prevOffset => {
        if (prevOffset >= maxOffset) {
          return 0;
        }
        if (prevOffset <= minOffset) {
          return -itemsPerVirtualPage;
        }
        return prevOffset;
      });
    };
    $[2] = itemsPerVirtualPage;
    $[3] = maxOffset;
    $[4] = minOffset;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onLoaded = t2;
  const {
    data: donationListData,
    isLoading
  } = useGetDonationListData(fundraisingEventId, itemsPerVirtualPage, cursor, onLoaded);
  let t3;
  if ($[6] !== donationListData) {
    t3 = donationListData || {};
    $[6] = donationListData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    receivedDonationsCount,
    confirmedDonationsAsc: confirmedDonations
  } = t3;
  let t4;
  if ($[8] !== confirmedDonations?.nodes) {
    t4 = confirmedDonations?.nodes?.slice().reverse() || [];
    $[8] = confirmedDonations?.nodes;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const donations = t4 as Donation[];
  let t5;
  if ($[10] !== displayableDonations || $[11] !== donations || $[12] !== itemsPerVirtualPage || $[13] !== offset) {
    t5 = displayableDonations.length ? displayableDonations : donations?.slice(offset, offset + itemsPerVirtualPage);
    $[10] = displayableDonations;
    $[11] = donations;
    $[12] = itemsPerVirtualPage;
    $[13] = offset;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const donationsToDisplay = t5;
  const totalVirtualPageCount = receivedDonationsCount ? Math.ceil(receivedDonationsCount / itemsPerVirtualPage) : 0;
  const actualVirtualPageIndex = virtualPageIndex >= 0 ? virtualPageIndex : totalVirtualPageCount - 1;
  const hasOlderDonations = actualVirtualPageIndex > 0;
  const hasNewerDonations = totalVirtualPageCount ? actualVirtualPageIndex + 1 < totalVirtualPageCount : false;
  const shouldGetOlderDonations = hasOlderDonations && offset < maxOffset;
  const shouldGetNewerDonations = hasNewerDonations && offset > minOffset;
  let t6;
  if ($[15] !== actualVirtualPageIndex || $[16] !== itemsPerVirtualPage || $[17] !== receivedDonationsCount || $[18] !== totalVirtualPageCount) {
    t6 = () => {
      if (!receivedDonationsCount) {
        return;
      }
      return receivedDonationsCount - itemsPerVirtualPage * (totalVirtualPageCount - actualVirtualPageIndex - 1);
    };
    $[15] = actualVirtualPageIndex;
    $[16] = itemsPerVirtualPage;
    $[17] = receivedDonationsCount;
    $[18] = totalVirtualPageCount;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const calculateNumericCursorForVirtualPage = t6;
  let t7;
  if ($[20] !== donations || $[21] !== offset) {
    t7 = isGettingOlderDonations => {
      const isSwitchingDirections = isGettingOlderDonations && offset < 0 || !isGettingOlderDonations && offset >= 0;
      if (!isSwitchingDirections) {
        return offset;
      }
      return isGettingOlderDonations ? donations.length + offset : -(donations.length - offset);
    };
    $[20] = donations;
    $[21] = offset;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const adjustOffset = t7;
  let t8;
  if ($[23] !== adjustOffset || $[24] !== itemsPerVirtualPage) {
    t8 = isGettingOlderDonations_0 => {
      const adjustedOffset = adjustOffset(isGettingOlderDonations_0);
      return isGettingOlderDonations_0 ? adjustedOffset + itemsPerVirtualPage : adjustedOffset - itemsPerVirtualPage;
    };
    $[23] = adjustOffset;
    $[24] = itemsPerVirtualPage;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const getNewOffset = t8;
  let t9;
  if ($[26] !== donations || $[27] !== itemsPerVirtualPage || $[28] !== offset || $[29] !== previouslyDisplayedDonations) {
    t9 = newOffset => {
      const isSwitchingDirections_0 = offset < 0 && newOffset >= 0 || offset >= 0 && newOffset < 0;
      if (isSwitchingDirections_0) {
        return previouslyDisplayedDonations;
      }
      const newDonations = newOffset === -itemsPerVirtualPage ? donations.slice(newOffset) : donations.slice(newOffset, newOffset + itemsPerVirtualPage);
      return newDonations;
    };
    $[26] = donations;
    $[27] = itemsPerVirtualPage;
    $[28] = offset;
    $[29] = previouslyDisplayedDonations;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const getDonationsToDisplay = t9;
  let t10;
  if ($[31] !== calculateNumericCursorForVirtualPage || $[32] !== cursor || $[33] !== itemsPerVirtualPage) {
    t10 = (isGettingOlderDonations_1, newOffset_0) => {
      const calculatedNumericCursor = calculateNumericCursorForVirtualPage();
      if (!calculatedNumericCursor) {
        return cursor;
      }
      const isOutOfRage = isGettingOlderDonations_1 && newOffset_0 === 0 || !isGettingOlderDonations_1 && newOffset_0 <= -itemsPerVirtualPage;
      const shift = isOutOfRage ? 5 : 1;
      const newNumericCursor = isGettingOlderDonations_1 ? calculatedNumericCursor - shift * itemsPerVirtualPage + 1 : calculatedNumericCursor + shift * itemsPerVirtualPage + 1;
      const newEncodedCursor = btoa(String(newNumericCursor));
      return newEncodedCursor;
    };
    $[31] = calculateNumericCursorForVirtualPage;
    $[32] = cursor;
    $[33] = itemsPerVirtualPage;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const getNewCursor = t10;
  let t11;
  if ($[35] !== donations || $[36] !== isLoading || $[37] !== itemsPerVirtualPage) {
    t11 = newOffset_1 => {
      if (isLoading) {
        return false;
      }
      const isGettingOlderDonations_2 = newOffset_1 >= 0;
      const isApproachingEdge = isGettingOlderDonations_2 ? donations.length - newOffset_1 < 3 * itemsPerVirtualPage : donations.length + newOffset_1 < 2 * itemsPerVirtualPage;
      const hasNoPreloadedData = isGettingOlderDonations_2 ? newOffset_1 === 0 : newOffset_1 === -itemsPerVirtualPage;
      return isApproachingEdge || hasNoPreloadedData;
    };
    $[35] = donations;
    $[36] = isLoading;
    $[37] = itemsPerVirtualPage;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const shouldPreloadDonations = t11;
  let t12;
  if ($[39] !== actualVirtualPageIndex || $[40] !== donationsToDisplay || $[41] !== getDonationsToDisplay || $[42] !== getNewCursor || $[43] !== getNewOffset || $[44] !== shouldGetOlderDonations || $[45] !== shouldPreloadDonations) {
    t12 = () => {
      if (!shouldGetOlderDonations) {
        return;
      }
      const newOffset_2 = getNewOffset(true);
      const shouldLoadNewData = shouldPreloadDonations(newOffset_2);
      const newDonations_0 = getDonationsToDisplay(newOffset_2);
      setDisplayableDonations(previousDonations => {
        setPreviouslyDisplayedDonations(previousDonations.length ? previousDonations : donationsToDisplay);
        return newDonations_0;
      });
      setVirtualPageIndex(actualVirtualPageIndex - 1);
      if (shouldLoadNewData) {
        const newCursor = getNewCursor(true, newOffset_2);
        setCursor(newCursor);
        setOffset(0);
      } else {
        setOffset(newOffset_2);
      }
    };
    $[39] = actualVirtualPageIndex;
    $[40] = donationsToDisplay;
    $[41] = getDonationsToDisplay;
    $[42] = getNewCursor;
    $[43] = getNewOffset;
    $[44] = shouldGetOlderDonations;
    $[45] = shouldPreloadDonations;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  const getOlderDonations = t12;
  let t13;
  if ($[47] !== actualVirtualPageIndex || $[48] !== getDonationsToDisplay || $[49] !== getNewCursor || $[50] !== getNewOffset || $[51] !== itemsPerVirtualPage || $[52] !== shouldGetNewerDonations || $[53] !== shouldPreloadDonations) {
    t13 = () => {
      if (!shouldGetNewerDonations) {
        return;
      }
      const newVirtualPageIndex = actualVirtualPageIndex + 1;
      const newOffset_3 = getNewOffset(false);
      const shouldLoadNewData_0 = shouldPreloadDonations(newOffset_3);
      const newDonations_1 = getDonationsToDisplay(newOffset_3);
      setDisplayableDonations(previousDonations_0 => {
        setPreviouslyDisplayedDonations(previousDonations_0);
        return newDonations_1;
      });
      setVirtualPageIndex(newVirtualPageIndex);
      if (shouldLoadNewData_0) {
        const newCursor_0 = getNewCursor(false, newOffset_3);
        setCursor(newCursor_0);
        setOffset(-itemsPerVirtualPage);
      } else {
        setOffset(newOffset_3);
      }
    };
    $[47] = actualVirtualPageIndex;
    $[48] = getDonationsToDisplay;
    $[49] = getNewCursor;
    $[50] = getNewOffset;
    $[51] = itemsPerVirtualPage;
    $[52] = shouldGetNewerDonations;
    $[53] = shouldPreloadDonations;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  const getNewerDonations = t13;
  let t14;
  if ($[55] !== donationsToDisplay || $[56] !== getNewerDonations || $[57] !== getOlderDonations || $[58] !== hasNewerDonations || $[59] !== isLoading || $[60] !== receivedDonationsCount || $[61] !== shouldGetNewerDonations || $[62] !== shouldGetOlderDonations) {
    t14 = {
      receivedDonationsCount,
      donationsToDisplay,
      hasNewerDonations,
      getOlderDonations,
      getNewerDonations,
      shouldGetOlderDonations,
      shouldGetNewerDonations,
      loading: isLoading
    };
    $[55] = donationsToDisplay;
    $[56] = getNewerDonations;
    $[57] = getOlderDonations;
    $[58] = hasNewerDonations;
    $[59] = isLoading;
    $[60] = receivedDonationsCount;
    $[61] = shouldGetNewerDonations;
    $[62] = shouldGetOlderDonations;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  return t14;
}
export default useDonationListData;